import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import { BookOpen, Search, ArrowLeft, Eye } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CourseCatalog() {
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [enrolledCourseIds, setEnrolledCourseIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCourses();
    if (user && user.role === 'student') {
      fetchEnrolledCourses();
    }
  }, [user]);

  useEffect(() => {
    if (searchQuery) {
      const filtered = courses.filter(
        (course) =>
          course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          course.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
          course.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredCourses(filtered);
    } else {
      setFilteredCourses(courses);
    }
  }, [searchQuery, courses]);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses`);
      setCourses(response.data);
      setFilteredCourses(response.data);
    } catch (error) {
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const fetchEnrolledCourses = async () => {
    try {
      const response = await axios.get(`${API}/enrollments/my-courses`, getAuthHeader());
      const enrolledIds = response.data.map(enrollment => enrollment.course_id);
      setEnrolledCourseIds(enrolledIds);
    } catch (error) {
      console.error('Failed to fetch enrolled courses');
    }
  };

  const isEnrolled = (courseId) => {
    return enrolledCourseIds.includes(courseId);
  };

  const handleEnroll = async (courseId, coursePrice) => {
    if (!user) {
      toast.error('Please login to enroll');
      navigate('/login');
      return;
    }

    if (user.role !== 'student') {
      toast.error('Only students can enroll in courses');
      return;
    }

    // If course is free (price <= 0), enroll directly
    if (coursePrice <= 0) {
      try {
        await axios.post(
          `${API}/enrollments`,
          { course_id: courseId },
          getAuthHeader()
        );
        setEnrolledCourseIds([...enrolledCourseIds, courseId]);
        toast.success('🎉 You have successfully enrolled in this course!', {
          duration: 3000,
          description: 'Redirecting to course content...'
        });
        setTimeout(() => {
          navigate(`/course/${courseId}`);
        }, 1500);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Enrollment failed');
      }
      return;
    }

    // For paid courses, redirect to payment
    try {
      toast.info('Redirecting to payment...', { duration: 2000 });
      
      const response = await axios.post(
        `${API}/payments/checkout`,
        { 
          course_id: courseId,
          origin_url: window.location.origin
        },
        getAuthHeader()
      );
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    }
  };

  const handleViewCourse = (courseId) => {
    navigate(`/course/${courseId}`);
  };

  const courseImages = [
    'https://images.unsplash.com/photo-1747654804207-9bfb6cde98f7?crop=entropy&cs=srgb&fm=jpg&q=85',
    'https://images.unsplash.com/photo-1515879218367-8466d910aaa4?crop=entropy&cs=srgb&fm=jpg&q=85',
    'https://images.unsplash.com/photo-1758685848061-3080d0780285?crop=entropy&cs=srgb&fm=jpg&q=85'
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Button
                data-testid="back-btn"
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center space-x-2">
                <BookOpen className="w-8 h-8 text-indigo-600" />
                <span className="text-2xl font-extrabold text-slate-900">EduFlow</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4">Course Catalog</h1>
          <div className="relative max-w-xl">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              data-testid="course-search-input"
              type="text"
              placeholder="Search courses by title, subject..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 rounded-lg border-slate-200 focus:border-indigo-500 focus:ring-indigo-500/20 bg-white"
            />
          </div>
        </div>

        {/* Courses Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCourses.length === 0 ? (
            <p className="text-slate-500 col-span-full text-center py-12">No courses found</p>
          ) : (
            filteredCourses.map((course, index) => (
              <Card
                key={course.id}
                data-testid={`course-card-${course.id}`}
                className="bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden card-hover"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={course.thumbnail || courseImages[index % courseImages.length]}
                    alt={course.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-3 right-3">
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/90 text-indigo-600">
                      {course.subject}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{course.title}</h3>
                  <p className="text-sm text-slate-600 mb-4 line-clamp-2">{course.description}</p>
                  
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-xs text-slate-500">Duration</p>
                      <p className="text-sm font-medium text-slate-900">{course.duration}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Students</p>
                      <p className="text-sm font-medium text-slate-900">{course.enrolled_count}</p>
                    </div>
                  </div>

                  {course.teacher_name && (
                    <p className="text-xs text-slate-500 mb-4">by {course.teacher_name}</p>
                  )}

                  <div className="flex items-center justify-between">
                    <div>
                      {course.price > 0 ? (
                        <span className="text-2xl font-bold text-indigo-600">₹{course.price}</span>
                      ) : (
                        <span className="text-xl font-bold text-teal-600">Free</span>
                      )}
                    </div>
                    {isEnrolled(course.id) ? (
                      <Button
                        data-testid={`view-course-btn-${course.id}`}
                        onClick={() => handleViewCourse(course.id)}
                        className="rounded-full bg-teal-600 hover:bg-teal-700 text-white px-6 shadow-lg shadow-teal-500/20 hover:-translate-y-0.5 transition-all duration-200"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View Course
                      </Button>
                    ) : (
                      <Button
                        data-testid={`enroll-btn-${course.id}`}
                        onClick={() => handleEnroll(course.id, course.price)}
                        className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white px-6 shadow-lg shadow-indigo-500/20 hover:-translate-y-0.5 transition-all duration-200"
                        disabled={user?.role !== 'student'}
                      >
                        {course.price > 0 ? 'Buy Now' : 'Enroll Free'}
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
